// Libraries
import * as Sentry from '@sentry/nextjs';

/** sessionStorage key set once the probe has run in this tab, so a page change does not re-run it. */
export const DOMAIN_CANARY_RAN_KEY = 'domain-canary-ran';

/** How long a probe waits for a response before reporting the host as blocked. */
export const DOMAIN_CANARY_TIMEOUT_MS = 10_000;

/** Sentry message name shared by every probe result; filter on it in Discover. */
export const DOMAIN_CANARY_MESSAGE = 'domain-canary';

/** The URLs probed on every sign-in: the app and API hostnames the CII app is moving to. */
export const DOMAIN_CANARY_URLS = ['https://app.bearing.ai/canary.json', 'https://api.bearing.ai/'];

/**
 * JSON body each canary host is known to answer with. A 2xx whose body does not carry these fields is
 * reported as `unexpected_body`, which is what a firewall or captive portal looks like when it swaps in
 * its own 200 page instead of dropping the request. Hosts not listed here are judged on status alone.
 */
export const DOMAIN_CANARY_EXPECTED_BODIES: Record<string, Record<string, unknown>> = {
  'app.bearing.ai': { ok: true, service: 'liner-ui' },
  'api.bearing.ai': { detail: 'Success' },
};

/** How many characters of an unexpected body are attached to the Sentry event. */
const BODY_SNIPPET_LENGTH = 200;

/** Outcome of one probe: a 2xx with the expected body, a 2xx with another body, another status, or no response. */
export type DomainCanaryResult = 'ok' | 'unexpected_body' | 'http_error' | 'blocked';

/** What one probe found, before it is sent to Sentry. */
type DomainCanaryReport = {
  url: string;
  result: DomainCanaryResult;
  ms: number;
  status?: number;
  error?: string;
  message?: string;
  body?: string;
};

/** Names the failure the way Sentry filters read it: a timeout, a network-level failure, or the error's own name. */
const errorTag = (error: unknown) => {
  const name = typeof error === 'object' && error !== null ? (error as { name?: unknown }).name : undefined;
  if (typeof name !== 'string' || !name) return 'unknown';
  if (name === 'AbortError') return 'timeout';
  if (name === 'TypeError') return 'network';
  return name;
};

/** True when the body parses as JSON and carries every expected field with the expected value. */
const matchesExpectedBody = (body: string, expected: Record<string, unknown>) => {
  try {
    const parsed: unknown = JSON.parse(body);
    if (typeof parsed !== 'object' || parsed === null) return false;
    return Object.entries(expected).every(([key, value]) => (parsed as Record<string, unknown>)[key] === value);
  } catch {
    return false;
  }
};

const probe = async (url: string, fetchFn: typeof fetch): Promise<DomainCanaryReport> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), DOMAIN_CANARY_TIMEOUT_MS);
  const started = performance.now();
  const elapsed = () => Math.round(performance.now() - started);

  try {
    const response = await fetchFn(`${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });
    const { status } = response;
    if (!response.ok) return { url, result: 'http_error', status, ms: elapsed() };

    const expected = DOMAIN_CANARY_EXPECTED_BODIES[new URL(url).host];
    if (!expected) return { url, result: 'ok', status, ms: elapsed() };

    const body = await response.text();
    if (matchesExpectedBody(body, expected)) return { url, result: 'ok', status, ms: elapsed() };
    return { url, result: 'unexpected_body', status, body: body.slice(0, BODY_SNIPPET_LENGTH), ms: elapsed() };
  } catch (error) {
    return { url, result: 'blocked', error: errorTag(error), message: String(error), ms: elapsed() };
  } finally {
    clearTimeout(timer);
  }
};

const report = ({ url, result, status, error, message, body, ms }: DomainCanaryReport) => {
  const host = new URL(url).host;
  Sentry.captureMessage(DOMAIN_CANARY_MESSAGE, {
    level: result === 'ok' ? 'info' : 'warning',
    // One Sentry issue per host and outcome, so "app.bearing.ai blocked" is its own row with its own user list.
    fingerprint: [DOMAIN_CANARY_MESSAGE, host, result],
    tags: {
      canary_host: host,
      canary_result: result,
      canary_origin: window.location.host,
      ...(status !== undefined && { canary_status: String(status) }),
      ...(error && { canary_error: error }),
    },
    extra: { url, ms, ...(message && { message }), ...(body !== undefined && { body }) },
  });
};

/**
 * Temporary reachability probe for the domains the app is moving to. Fetches each canary URL once per
 * tab session and reports the outcome to Sentry, successes included, so Sentry shows per customer
 * whether their network lets the new hostnames through before anyone is moved. A host is only `ok`
 * when it answers 2xx with the body listed in `DOMAIN_CANARY_EXPECTED_BODIES`. Never throws and never
 * blocks the page.
 * @param {string[]} urls - Full canary URLs; defaults to `DOMAIN_CANARY_URLS`.
 * @param {typeof fetch} fetchFn - Injectable for tests.
 */
export const runDomainCanary = async (
  urls: string[] = DOMAIN_CANARY_URLS,
  fetchFn: typeof fetch = (...args) => fetch(...args)
) => {
  if (sessionStorage.getItem(DOMAIN_CANARY_RAN_KEY)) return;
  sessionStorage.setItem(DOMAIN_CANARY_RAN_KEY, String(Date.now()));

  const reports = await Promise.all(urls.map((url) => probe(url, fetchFn)));
  reports.forEach(report);
};
